import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft, Wallet, Loader2, ArrowDownToLine, History, ShieldCheck,
  ShieldAlert, Smartphone, Upload, Camera, AlertCircle, CheckCircle,
  XCircle, Clock, Banknote, Mail, Globe
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending:    { icon: Clock,       color: 'text-amber-500',   bg: 'bg-amber-500/10',   label: 'Pending' },
  processing: { icon: Clock,       color: 'text-amber-500',   bg: 'bg-amber-500/10',   label: 'Processing' },
  approved:   { icon: ShieldCheck, color: 'text-blue-500',    bg: 'bg-blue-500/10',    label: 'Approved' },
  completed:  { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-500/10', label: 'Completed' },
  rejected:   { icon: XCircle,     color: 'text-red-500',     bg: 'bg-red-500/10',     label: 'Rejected' },
  failed:     { icon: XCircle,     color: 'text-red-500',     bg: 'bg-red-500/10',     label: 'Failed' },
};

const METHODS = {
  moncash: { label: 'MonCash',  haiti: true,  icon: Smartphone, desc: 'Haiti mobile money' },
  natcash: { label: 'NatCash',  haiti: true,  icon: Smartphone, desc: 'Haiti mobile money' },
  paypal:  { label: 'PayPal',   haiti: false, icon: Mail,       desc: 'International' },
};

// Auto-detect if the user is in Haiti from browser timezone.
function detectIsHaiti() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    return tz === 'America/Port-au-Prince';
  } catch { return false; }
}

function StatusBadge({ status }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.pending;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${s.color} ${s.bg}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

export default function WithdrawalPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();

  // Country detection — auto-detect from timezone, user can override.
  const [country, setCountry] = useState(() => detectIsHaiti() ? 'HT' : 'INTL');

  const [limits, setLimits] = useState(null);
  const [kyc, setKyc] = useState(null);
  const [creatorEarnings, setCreatorEarnings] = useState(null); // legacy PayPal balance
  const [history, setHistory] = useState([]);          // new unified withdrawals
  const [legacyHistory, setLegacyHistory] = useState([]); // legacy PayPal withdrawals
  const [loading, setLoading] = useState(true);

  // Available methods adapt to country.
  const availableMethods = useMemo(() => {
    if (country === 'HT') {
      // Haitian users see MOBILE first, then international fallback
      return ['moncash', 'natcash', 'paypal'];
    }
    return ['paypal']; // International users — MonCash/NatCash not relevant
  }, [country]);

  // Form state
  const [method, setMethod] = useState(availableMethods[0]);
  const [phone, setPhone] = useState('');
  const [paypalEmail, setPaypalEmail] = useState(user?.email || '');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // Keep method valid when country changes
  useEffect(() => {
    if (!availableMethods.includes(method)) setMethod(availableMethods[0]);
  }, [availableMethods, method]);

  const fetchAll = useCallback(async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [lim, k, h, le, lh] = await Promise.all([
        axios.get(`${API}/withdrawals/limits`, { headers }),
        axios.get(`${API}/withdrawals/kyc/status`, { headers }),
        axios.get(`${API}/withdrawals/history`, { headers }),
        axios.get(`${API}/creator/earnings`, { headers }).catch(() => ({ data: null })),
        axios.get(`${API}/creator/withdrawals`, { headers }).catch(() => ({ data: [] })),
      ]);
      setLimits(lim.data);
      setKyc(k.data);
      setHistory(h.data || []);
      setCreatorEarnings(le.data);
      setLegacyHistory(lh.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Could not load withdrawal data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleWithdraw = async (e) => {
    e?.preventDefault?.();
    const amt = parseFloat(amount);
    if (!amt || amt < (limits?.min_usd || 5)) {
      toast.error(`Minimum is $${limits?.min_usd || 5}`);
      return;
    }
    setSubmitting(true);
    try {
      const payload = { amount_usd: amt, method, note };
      if (method === 'paypal') payload.paypal_email = paypalEmail;
      else payload.phone = phone;
      await axios.post(`${API}/withdrawals/request`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Withdrawal request submitted!');
      setAmount(''); setNote('');
      fetchAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const kycStatus = kyc?.status || 'none';
  const kycApproved = kycStatus === 'approved';
  const rate = limits?.exchange_rate?.htg_per_usd || 132;
  const methodRequiresKyc = ['moncash', 'natcash'].includes(method);
  const canSubmit = methodRequiresKyc ? kycApproved : true;
  const netUsd = amount ? (parseFloat(amount) * (1 - (limits?.platform_fee_pct || 0.4))) : 0;
  const netHtg = netUsd * rate;
  const amtHtg = amount ? parseFloat(amount) * rate : 0;

  // Merge legacy + new withdrawals for unified history view
  const allHistory = [
    ...history.map(w => ({ ...w, _system: 'new' })),
    ...legacyHistory.map(w => ({
      ...w,
      _system: 'legacy',
      amount_usd: w.amount,
      method: 'paypal',
    })),
  ].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="flex items-center gap-3 p-4 max-w-3xl mx-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold">Withdraw Funds</h1>
            <p className="text-xs text-muted-foreground">PayPal · MonCash · NatCash</p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto p-4 space-y-6">
        {/* Country selector */}
        <Card data-testid="country-card">
          <CardContent className="pt-4 pb-4 flex items-center gap-3">
            <Globe className="w-5 h-5 text-primary flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold">Your region</p>
              <p className="text-xs text-muted-foreground">
                {country === 'HT' ? 'Haiti — all methods available' : 'International — PayPal & cards'}
              </p>
            </div>
            <div className="flex gap-1">
              <button
                onClick={() => setCountry('HT')}
                data-testid="country-ht"
                className={`px-3 py-1 rounded-full text-xs font-bold transition
                  ${country === 'HT' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}
              >🇭🇹 Haiti</button>
              <button
                onClick={() => setCountry('INTL')}
                data-testid="country-intl"
                className={`px-3 py-1 rounded-full text-xs font-bold transition
                  ${country === 'INTL' ? 'bg-primary text-primary-foreground' : 'bg-secondary text-muted-foreground'}`}
              >🌍 Other</button>
            </div>
          </CardContent>
        </Card>

        {/* Balance card */}
        <Card data-testid="balance-card" className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/30">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Wallet className="w-4 h-4" /> Available Balance
                </p>
                <p className="text-4xl font-bold text-emerald-500" data-testid="available-usd">
                  ${limits?.available_usd?.toFixed(2) || '0.00'}
                </p>
                <p className="text-sm text-muted-foreground mt-1">
                  {limits?.available_coins?.toLocaleString() || 0} coins
                  {country === 'HT' && ` · ~${((limits?.available_usd || 0) * rate).toFixed(0)} HTG`}
                </p>
                {creatorEarnings?.is_creator && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Creator earnings balance: ${creatorEarnings.available_balance?.toFixed(2)} (PayPal-only legacy)
                  </p>
                )}
              </div>
              <Banknote className="w-12 h-12 text-emerald-500/30" />
            </div>
          </CardContent>
        </Card>

        {/* KYC banner — only when user is targeting MoMo methods or in Haiti */}
        {country === 'HT' && (
          <KYCSection kyc={kyc} kycStatus={kycStatus} token={token} onUpdated={fetchAll} />
        )}

        <Tabs defaultValue="withdraw" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="withdraw" data-testid="tab-withdraw">
              <ArrowDownToLine className="w-4 h-4 mr-2" /> Withdraw
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <History className="w-4 h-4 mr-2" /> History ({allHistory.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="withdraw" className="space-y-4">
            {methodRequiresKyc && !kycApproved && (
              <Card className="border-amber-500/30 bg-amber-500/5">
                <CardContent className="pt-4 pb-4 flex items-center gap-3">
                  <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                  <p className="text-sm">Complete identity verification above to use MonCash/NatCash. PayPal does not require KYC.</p>
                </CardContent>
              </Card>
            )}

            <Card className={!canSubmit ? 'opacity-60 pointer-events-none' : ''}>
              <CardHeader>
                <CardTitle>New Withdrawal</CardTitle>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleWithdraw} className="space-y-4">
                  <div>
                    <Label>Payment Method</Label>
                    <div className={`grid gap-2 mt-2 ${availableMethods.length === 1 ? 'grid-cols-1' : 'grid-cols-2 sm:grid-cols-3'}`}>
                      {availableMethods.map((m) => {
                        const meta = METHODS[m];
                        const Icon = meta.icon;
                        return (
                          <button
                            key={m}
                            type="button"
                            onClick={() => setMethod(m)}
                            data-testid={`method-${m}`}
                            className={`p-3 rounded-xl border-2 transition flex flex-col items-center justify-center gap-1 font-semibold
                              ${method === m
                                ? 'border-primary bg-primary/10 text-primary'
                                : 'border-border hover:bg-secondary'}`}
                          >
                            <Icon className="w-4 h-4" />
                            <span className="text-sm">{meta.label}</span>
                            <span className="text-[10px] text-muted-foreground font-normal">{meta.desc}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Destination field — phone for MoMo, email for PayPal */}
                  {method === 'paypal' ? (
                    <div>
                      <Label htmlFor="paypal-email">PayPal Email</Label>
                      <Input
                        id="paypal-email"
                        type="email"
                        value={paypalEmail}
                        onChange={(e) => setPaypalEmail(e.target.value)}
                        placeholder="you@paypal.com"
                        data-testid="paypal-email-input"
                      />
                    </div>
                  ) : (
                    <div>
                      <Label htmlFor="phone">Phone Number (+509)</Label>
                      <Input
                        id="phone"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+509 XXXX XXXX"
                        data-testid="phone-input"
                      />
                    </div>
                  )}

                  <div>
                    <Label htmlFor="amount">Amount (USD)</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder={`Min $${limits?.min_usd}, Max $${limits?.max_per_tx_usd}`}
                      data-testid="amount-input"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Weekly remaining: ${limits?.weekly_remaining_usd?.toFixed(2)}
                    </p>
                  </div>

                  {amount && parseFloat(amount) >= (limits?.min_usd || 5) && (
                    <div className="p-3 bg-secondary/50 rounded-xl space-y-1 text-sm" data-testid="breakdown">
                      <div className="flex justify-between">
                        <span>Amount:</span>
                        <span>${parseFloat(amount).toFixed(2)}{method !== 'paypal' && ` ≈ ${amtHtg.toFixed(0)} HTG`}</span>
                      </div>
                      <div className="flex justify-between text-muted-foreground">
                        <span>Platform fee ({Math.round((limits?.platform_fee_pct || 0.4) * 100)}%):</span>
                        <span>-${(parseFloat(amount) * (limits?.platform_fee_pct || 0.4)).toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between font-bold text-emerald-500 border-t border-border pt-1">
                        <span>You'll receive:</span>
                        <span>${netUsd.toFixed(2)}{method !== 'paypal' && ` ≈ ${netHtg.toFixed(0)} HTG`}</span>
                      </div>
                      {method !== 'paypal' && (
                        <p className="text-[10px] text-muted-foreground">
                          Rate: 1 USD = {rate} HTG ({limits?.exchange_rate?.source})
                        </p>
                      )}
                    </div>
                  )}

                  <div>
                    <Label htmlFor="note">Note (optional)</Label>
                    <Input
                      id="note"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      maxLength={200}
                      placeholder="e.g., for groceries"
                      data-testid="note-input"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={submitting || !canSubmit}
                    className="w-full"
                    data-testid="submit-withdraw"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ArrowDownToLine className="w-4 h-4 mr-2" />}
                    Request Withdrawal
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history" className="space-y-2" data-testid="history-list">
            {allHistory.length === 0 ? (
              <Card>
                <CardContent className="py-8 text-center text-muted-foreground">
                  No withdrawals yet.
                </CardContent>
              </Card>
            ) : (
              allHistory.map((w) => (
                <Card key={w.id} data-testid={`withdrawal-${w.id}`}>
                  <CardContent className="pt-4 pb-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-bold text-lg">${(w.amount_usd ?? w.amount)?.toFixed(2)}</p>
                          <StatusBadge status={w.status} />
                          <span className="text-xs px-2 py-0.5 rounded-full bg-secondary uppercase font-semibold">
                            {w.method || 'paypal'}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-1">
                          {w.method === 'paypal' || w._system === 'legacy'
                            ? (w.paypal_email || '')
                            : `${w.phone} · You receive: $${w.net_usd?.toFixed(2)} (${w.net_htg?.toFixed(0)} HTG)`}
                        </p>
                        {w.status === 'rejected' && w.rejection_reason && (
                          <p className="text-xs text-red-500 mt-1">Reason: {w.rejection_reason}</p>
                        )}
                        {w.status === 'completed' && w.transaction_reference && (
                          <p className="text-xs text-emerald-500 mt-1">Ref: {w.transaction_reference}</p>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground flex-shrink-0">
                        {new Date(w.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ============== KYC Component (unchanged from prev — Haiti only) ==============
function KYCSection({ kyc, kycStatus, token, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: '', id_number: '', phone: '', dob: '',
    id_document_url: '', selfie_url: '',
  });
  const [uploading, setUploading] = useState({ id: false, selfie: false });
  const idFileRef = useRef();
  const selfieFileRef = useRef();

  const uploadToCloudinary = async (file, kind) => {
    setUploading((u) => ({ ...u, [kind]: true }));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/upload/media`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = r.data?.url || r.data?.secure_url;
      if (kind === 'id') setForm((f) => ({ ...f, id_document_url: url }));
      else setForm((f) => ({ ...f, selfie_url: url }));
      toast.success(`${kind === 'id' ? 'ID' : 'Selfie'} uploaded`);
    } catch (e) {
      toast.error('Upload failed');
    } finally {
      setUploading((u) => ({ ...u, [kind]: false }));
    }
  };

  const handleSubmit = async () => {
    if (!form.id_document_url || !form.selfie_url) {
      toast.error('Please upload both ID and selfie');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/withdrawals/kyc/submit`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Submitted! We will review within 24h.');
      setOpen(false);
      onUpdated?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Submission failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (kycStatus === 'approved') {
    return (
      <Card className="border-emerald-500/30 bg-emerald-500/5" data-testid="kyc-approved">
        <CardContent className="pt-4 pb-4 flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-500" />
          <p className="text-sm">Identity verified — MonCash & NatCash unlocked.</p>
        </CardContent>
      </Card>
    );
  }

  if (kycStatus === 'pending') {
    return (
      <Card className="border-amber-500/30 bg-amber-500/5" data-testid="kyc-pending">
        <CardContent className="pt-4 pb-4 flex items-center gap-3">
          <Clock className="w-5 h-5 text-amber-500" />
          <div>
            <p className="text-sm font-semibold">Verification under review</p>
            <p className="text-xs text-muted-foreground">PayPal still available while you wait.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={`${kycStatus === 'rejected' ? 'border-red-500/30 bg-red-500/5' : 'border-amber-500/30 bg-amber-500/5'}`} data-testid="kyc-banner">
      <CardContent className="pt-4 pb-4">
        <div className="flex items-start gap-3 mb-3">
          <ShieldAlert className={`w-5 h-5 ${kycStatus === 'rejected' ? 'text-red-500' : 'text-amber-500'} flex-shrink-0 mt-0.5`} />
          <div className="flex-1">
            <p className="text-sm font-semibold">
              {kycStatus === 'rejected' ? 'Verification rejected — please re-submit' : 'Identity verification (required for MonCash/NatCash only)'}
            </p>
            <p className="text-xs text-muted-foreground">
              Upload your government ID and selfie to unlock Haitian mobile money payouts.
              PayPal is available without KYC.
            </p>
            {kycStatus === 'rejected' && kyc?.rejection_reason && (
              <p className="text-xs text-red-500 mt-1">Reason: {kyc.rejection_reason}</p>
            )}
          </div>
        </div>
        {!open && (
          <Button onClick={() => setOpen(true)} size="sm" className="w-full" data-testid="start-kyc-btn">
            <Upload className="w-4 h-4 mr-2" />
            {kycStatus === 'rejected' ? 'Re-submit Verification' : 'Start Verification'}
          </Button>
        )}
        {open && (
          <div className="space-y-3 mt-3">
            <Input placeholder="Full legal name" value={form.full_name}
                   onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="kyc-name" />
            <Input placeholder="National ID / NIF number" value={form.id_number}
                   onChange={(e) => setForm({ ...form, id_number: e.target.value })} data-testid="kyc-id-number" />
            <Input placeholder="Phone +509 XXXXXXXX" value={form.phone}
                   onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="kyc-phone" />
            <Input type="date" placeholder="Date of birth" value={form.dob}
                   onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="kyc-dob" />

            <div className="grid grid-cols-2 gap-2">
              <input ref={idFileRef} type="file" accept="image/*" className="hidden"
                     onChange={(e) => e.target.files?.[0] && uploadToCloudinary(e.target.files[0], 'id')} />
              <Button type="button" variant={form.id_document_url ? 'default' : 'outline'}
                      onClick={() => idFileRef.current?.click()} disabled={uploading.id}
                      data-testid="upload-id">
                {uploading.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> :
                 form.id_document_url ? <CheckCircle className="w-4 h-4 mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
                ID Photo
              </Button>

              <input ref={selfieFileRef} type="file" accept="image/*" capture="user" className="hidden"
                     onChange={(e) => e.target.files?.[0] && uploadToCloudinary(e.target.files[0], 'selfie')} />
              <Button type="button" variant={form.selfie_url ? 'default' : 'outline'}
                      onClick={() => selfieFileRef.current?.click()} disabled={uploading.selfie}
                      data-testid="upload-selfie">
                {uploading.selfie ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> :
                 form.selfie_url ? <CheckCircle className="w-4 h-4 mr-2" /> : <Camera className="w-4 h-4 mr-2" />}
                Selfie
              </Button>
            </div>

            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => setOpen(false)} className="flex-1">Cancel</Button>
              <Button onClick={handleSubmit} disabled={submitting} className="flex-1" data-testid="kyc-submit-btn">
                {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Submit
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
